import React, { useEffect } from 'react';
import { Layout } from 'antd';
import { Outlet, useNavigate } from 'react-router';
import { UserStoreProvider } from '@/hooks/UserStoreContext';
import { useUserQuery } from '@/queries/user.query';
import { identifyUser, resetIntegrations } from '@/libs/integrations';
import AuthSide from './AuthSide';
import AuthLoading from './AuthLoading';
import { ApiError } from '@/queries/api-client';
import { ApiErrorContent } from '@/libs/api-error-handler';
import { checkEmbedMode } from '@/utils/dom';

const { Content } = Layout;

export const AuthLayout: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading, isError, error, data: user, refetch } = useUserQuery();
  const isEmbedMode = checkEmbedMode();

  useEffect(() => {
    if (!user) return;

    // Identify user with all integrations
    identifyUser(user);

    // Reset on cleanup (user logout)
    return () => {
      resetIntegrations();
    };
  }, [user]);

  if (isError && error) {
    const apiError = error as ApiError;
    if (apiError.isAuthError) {
      navigate('/login', {
        state: { redirect_url: window.location.pathname },
      });
      return;
    }

    return (
      <ApiErrorContent
        error={apiError}
        title="Authentication Error"
        onRetry={refetch}
      />
    );
  }

  let authContent = null;

  if (isLoading) {
    authContent = <AuthLoading />;
  } else if (isError) {
    authContent = null;
  } else if (user) {
    authContent = (
      <UserStoreProvider initialUser={user}>
        <Outlet />
      </UserStoreProvider>
    );
  }

  return (
    <Layout className="min-h-screen w-full flex-row">
      {!isEmbedMode && <AuthSide user={user} />}
      <Content
        id="auth-content"
        className="min-h-screen flex-1 overflow-x-auto"
      >
        {authContent}
      </Content>
    </Layout>
  );
};
